import {useMutation} from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import queryClient from '@/utils/reactQueryClient';
import {CreatorBrandSafetyVetting} from '@/types/creatorBrandSafetyVetting';
import vettingUpdaters from '@/api/updaters/vetting';
import getAvatarSrc from '@/views/vetting/vetting-hub/utils/getAvatarSrc';
import {queryKey as resultsQueryKey} from '@/hooks/queries/use-creator-brand-safety-vettings-query';

dayjs.extend(utc);

function useCreateBrandSafetyVetting({brandId}: {brandId: string}) {
  const mutationKey = ['vetting', 'create'];

  return useMutation({
    mutationKey,
    mutationFn: vettingUpdaters.create,
    onMutate: async (data) => {
      const previousData = queryClient.getQueryData([resultsQueryKey, brandId]);

      queryClient.setQueryData(
        [resultsQueryKey, brandId],
        (old: {data: CreatorBrandSafetyVetting[]} | undefined) => {
          const newVetting = {
            id: `temp_${data.creator.email}`,
            modashId: null,
            reportId: `temp_${data.creator.email}`,
            type: 'creator_brand_safety_vettings' as const,
            status: 'invited' as const,
            creatorName: data.creator.fullName ?? '',
            createdAt: dayjs().utc().format(),
            reportStatus: null,
            reportUpdatedAt: null,
            itemCount: null,
            postCount: null,
            creatorImageUrl: getAvatarSrc(data.creator),
            creatorEmail: data.creator.email || undefined,
            profileId: data.creator.id,
          };
          return old?.data && {...old, data: [...old.data, newVetting]};
        }
      );

      return previousData;
    },
    onError: (_err, _data, context) => {
      queryClient.setQueryData([resultsQueryKey, brandId], context);
    },
    onSettled: () => {
      queryClient.invalidateQueries([resultsQueryKey, brandId]);
    },
  });
}

export default useCreateBrandSafetyVetting;
